import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Loader2, MessageSquare, Plus, Search } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { listPacientes } from '@/services/pacientes'
import {
  CANAL_LABEL, CATEGORIA_MSG_LABEL, criarModelo, listModelos, listThreads,
  registrarRecebida, STATUS_THREAD_LABEL,
  type CanalMensagem, type CategoriaMensagem, type MensagemModelo, type ThreadComPaciente,
} from '@/services/mensagens'

type Aba = 'abertas' | 'encerradas' | 'todas'

export default function MensagensPage() {
  const { current } = useAuth()
  const [threads, setThreads] = useState<ThreadComPaciente[]>([])
  const [modelos, setModelos] = useState<MensagemModelo[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [aba, setAba] = useState<Aba>('abertas')
  const [categoria, setCategoria] = useState<CategoriaMensagem | 'todas'>('todas')

  // registrar recebida
  const [registrando, setRegistrando] = useState(false)
  const [pacienteId, setPacienteId] = useState('')
  const [pacienteNome, setPacienteNome] = useState('')
  const [buscaPaciente, setBuscaPaciente] = useState('')
  const [sugestoes, setSugestoes] = useState<{ id: string; nome: string }[]>([])
  const [catNova, setCatNova] = useState<CategoriaMensagem>('duvida_administrativa')
  const [canal, setCanal] = useState<CanalMensagem>('whatsapp')
  const [conteudo, setConteudo] = useState('')

  // modelos
  const [gerindoModelos, setGerindoModelos] = useState(false)
  const [modeloTitulo, setModeloTitulo] = useState('')
  const [modeloConteudo, setModeloConteudo] = useState('')

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    setErro(null)
    try {
      const [ts, ms] = await Promise.all([
        listThreads(current.workspace.id, { aba, categoria }),
        listModelos(current.workspace.id),
      ])
      setThreads(ts)
      setModelos(ms)
    } catch {
      setErro('Não foi possível carregar. A migração phase7 já foi aplicada?')
    } finally {
      setLoading(false)
    }
  }, [current, aba, categoria])

  useEffect(() => { carregar() }, [carregar])

  useEffect(() => {
    if (!current || buscaPaciente.trim().length < 2) { setSugestoes([]); return }
    const t = setTimeout(async () => {
      const ps = await listPacientes(current.workspace.id, { busca: buscaPaciente, situacao: 'todas' })
      setSugestoes(ps.slice(0, 8).map(p => ({ id: p.id, nome: p.nome })))
    }, 250)
    return () => clearTimeout(t)
  }, [current, buscaPaciente])

  async function registrar(e: React.FormEvent) {
    e.preventDefault()
    if (!current || !pacienteId || conteudo.trim().length < 2) return
    setBusy(true)
    try {
      await registrarRecebida({
        workspaceId: current.workspace.id,
        pacienteId,
        categoria: catNova,
        canal,
        conteudo,
      })
      setRegistrando(false)
      setPacienteId(''); setPacienteNome(''); setConteudo('')
      await carregar()
    } catch {
      setErro('Não foi possível registrar a mensagem.')
    } finally {
      setBusy(false)
    }
  }

  async function salvarModelo(e: React.FormEvent) {
    e.preventDefault()
    if (!current || modeloTitulo.trim().length < 2 || modeloConteudo.trim().length < 2) return
    setBusy(true)
    try {
      await criarModelo(current.workspace.id, modeloTitulo, modeloConteudo)
      setModeloTitulo(''); setModeloConteudo('')
      await carregar()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Mensagens</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Solicitações de pacientes, com triagem — nada chega direto sem controle.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={() => setGerindoModelos(v => !v)}>Modelos</button>
          <button className="btn-primary" onClick={() => setRegistrando(v => !v)}>
            <Plus size={16} aria-hidden /> Registrar mensagem
          </button>
        </div>
      </header>

      {gerindoModelos && (
        <form onSubmit={salvarModelo} className="card" style={{ padding: 16, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <strong style={{ fontSize: 14.5 }}>Modelos de resposta</strong>
          {modelos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {modelos.map(m => (
                <div key={m.id} style={{ fontSize: 13, borderLeft: '3px solid var(--primary-soft)', paddingLeft: 8 }}>
                  <strong>{m.titulo}</strong>
                  <div style={{ color: 'var(--text-secondary)' }}>{m.conteudo.slice(0, 140)}{m.conteudo.length > 140 ? '…' : ''}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <input className="field-input" placeholder="Título do modelo" value={modeloTitulo} onChange={e => setModeloTitulo(e.target.value)} aria-label="Título do modelo" />
          </div>
          <textarea className="field-input" rows={2} style={{ resize: 'vertical' }} placeholder="Conteúdo do modelo" value={modeloConteudo} onChange={e => setModeloConteudo(e.target.value)} aria-label="Conteúdo do modelo" />
          <div>
            <button type="submit" className="btn-primary" disabled={busy} style={{ minHeight: 38, fontSize: 13.5 }}>Salvar modelo</button>
          </div>
        </form>
      )}

      {registrando && (
        <form onSubmit={registrar} className="card" style={{ padding: 16, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <strong style={{ fontSize: 14.5 }}>Registrar mensagem recebida</strong>
          {pacienteId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="badge badge-info" style={{ fontSize: 13.5, padding: '8px 12px' }}>{pacienteNome}</span>
              <button type="button" className="btn-secondary" style={{ minHeight: 34, padding: '4px 10px', fontSize: 13 }} onClick={() => { setPacienteId(''); setPacienteNome('') }}>Trocar</button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <Search size={16} aria-hidden style={{ position: 'absolute', left: 12, top: 14, color: 'var(--muted)' }} />
              <input className="field-input" style={{ paddingLeft: 36 }} placeholder="Paciente" value={buscaPaciente} onChange={e => setBuscaPaciente(e.target.value)} autoComplete="off" aria-label="Buscar paciente" />
              {sugestoes.length > 0 && (
                <div className="card" style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: 48, boxShadow: 'var(--shadow-float)', overflow: 'hidden' }}>
                  {sugestoes.map(s => (
                    <button key={s.id} type="button" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', background: 'none', border: 'none', fontSize: 14, borderBottom: '1px solid var(--border)' }} onClick={() => { setPacienteId(s.id); setPacienteNome(s.nome); setSugestoes([]); setBuscaPaciente('') }}>
                      {s.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5 }} value={catNova} onChange={e => setCatNova(e.target.value as CategoriaMensagem)} aria-label="Categoria">
              {(Object.keys(CATEGORIA_MSG_LABEL) as CategoriaMensagem[]).map(c => (
                <option key={c} value={c}>{CATEGORIA_MSG_LABEL[c]}</option>
              ))}
            </select>
            <select className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5 }} value={canal} onChange={e => setCanal(e.target.value as CanalMensagem)} aria-label="Canal">
              {(Object.keys(CANAL_LABEL) as CanalMensagem[]).map(c => (
                <option key={c} value={c}>{CANAL_LABEL[c]}</option>
              ))}
            </select>
          </div>
          <textarea className="field-input" rows={3} style={{ resize: 'vertical' }} placeholder="O que a paciente disse?" value={conteudo} onChange={e => setConteudo(e.target.value)} aria-label="Conteúdo da mensagem" />
          <div>
            <button type="submit" className="btn-primary" disabled={busy || !pacienteId}>
              {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
              Registrar
            </button>
          </div>
        </form>
      )}

      <div className="card" style={{ padding: 12, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['abertas', 'encerradas', 'todas'] as Aba[]).map(a => (
          <button
            key={a} onClick={() => setAba(a)} aria-pressed={aba === a}
            className="badge"
            style={{
              cursor: 'pointer', fontSize: 13, padding: '7px 13px', border: '1px solid', textTransform: 'capitalize',
              background: aba === a ? 'var(--primary-soft)' : 'var(--card)',
              color: aba === a ? 'var(--primary-deep)' : 'var(--text-secondary)',
              borderColor: aba === a ? 'var(--primary)' : 'var(--border-input)',
            }}
          >
            {a}
          </button>
        ))}
        <select className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 13, marginLeft: 'auto' }} value={categoria} onChange={e => setCategoria(e.target.value as typeof categoria)} aria-label="Filtrar por categoria">
          <option value="todas">Toda categoria</option>
          {(Object.keys(CATEGORIA_MSG_LABEL) as CategoriaMensagem[]).map(c => (
            <option key={c} value={c}>{CATEGORIA_MSG_LABEL[c]}</option>
          ))}
        </select>
      </div>

      {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        </div>
      ) : threads.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <MessageSquare size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Nenhuma mensagem por aqui</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {threads.map(t => (
            <Link key={t.id} to={`/mensagens/${t.id}`} className="card" style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{t.paciente_nome}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                  {CATEGORIA_MSG_LABEL[t.categoria]}{t.assunto ? ` · ${t.assunto}` : ''}
                </div>
              </div>
              {t.prioridade !== 'normal' && (
                <span className={`badge ${t.prioridade === 'urgente' ? 'badge-danger' : t.prioridade === 'alta' ? 'badge-warn' : 'badge-muted'}`}>
                  {t.prioridade}
                </span>
              )}
              <span className={`badge ${t.status === 'respondida' || t.status === 'encerrada' ? 'badge-ok' : t.status === 'aguardando_aprovacao' || t.status === 'aguardando_medico' ? 'badge-warn' : 'badge-info'}`}>
                {STATUS_THREAD_LABEL[t.status]}
              </span>
              <ChevronRight size={16} aria-hidden style={{ color: 'var(--muted)', flexShrink: 0 }} />
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
